import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import NcImage from "shared/NcImage/NcImage";
import "react-loading-skeleton/dist/skeleton.css";
import Skeleton from "react-loading-skeleton";
import "./MessageList.scss";
import { RiAttachmentLine } from "react-icons/ri";
import { FiMessageSquare, FiChevronUp } from "react-icons/fi";

interface MessageBody {
  name?: string;
  url?: string | null;
}

interface Sender {
  id?: number;
  name?: string;
  image?: string | null;
}

interface Message {
  id: number;
  body: MessageBody;
  event_message: string | null;
  event_for: string;
  sender_id: number;
  receiver_id: number;
  sender: Sender;
  receiver: Sender;
  "read?": boolean;
  attachments: any[];
  created_at: string;
}

interface MessageGroup {
  date: string;
  messages: Message[];
}

interface ChatData {
  id: number;
  sender_id: number;
  receiver_id: number;
  sender: Sender;
  receiver: Sender;
  last_message: string;
  last_message_time: string;
  unread_count: number;
  property: any;
  booking: any;
  booking_query: any;
}

interface ApiResponse {
  data: string;
  chat: ChatData;
  message: MessageGroup[];
  success: boolean;
  meta: {
    total_pages: number;
    current_page: number;
    next_page: number | null;
    prev_page: number | null;
    total_count: number;
  };
}

interface MessageListProps {
  modifiedBookingMap: ApiResponse[] | ApiResponse;
  showMore: boolean;
  loadMoreMessages: (pge: number) => Promise<void>;
}

const formatMessageTime = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const AvatarFallback = ({ name }: { name?: string }) => (
  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#F56948] to-[#FF8A6E] flex items-center justify-center flex-shrink-0 ring-2 ring-white dark:ring-gray-800">
    <span className="text-xs font-bold text-white">
      {name ? name.charAt(0).toUpperCase() : "?"}
    </span>
  </div>
);

const SystemMessageBubble = ({ msg }: { msg: Message }) => {
  const text = msg.body?.name || "";
  const eventMsg = msg.event_message || "";

  const isCancellation = eventMsg === "booking canceled";
  const isRequest = eventMsg === "booking request";
  const isConfirmation = eventMsg === "booking confirmed";

  let colorScheme: "cancel" | "request" | "confirm" | "default" = "default";
  let title = "System Notification";

  if (isCancellation) {
    colorScheme = "cancel";
    title = text.toLowerCase().includes("you cancelled")
      ? "You Cancelled This Booking"
      : text.toLowerCase().includes("host")
      ? "Host Cancelled Booking"
      : "Booking Cancelled";
  } else if (isRequest) {
    colorScheme = "request";
    title = "Booking Request";
  } else if (isConfirmation) {
    colorScheme = "confirm";
    title = "Booking Confirmed";
  }

  const schemeStyles = {
    cancel: {
      wrapper:
        "bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/30 border-red-300 dark:border-red-700",
      title: "text-red-700 dark:text-red-300",
      body: "text-red-900 dark:text-red-100",
      icon: (
        <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      ),
    },
    request: {
      wrapper:
        "bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 border-blue-300 dark:border-blue-700",
      title: "text-blue-700 dark:text-blue-300",
      body: "text-blue-900 dark:text-blue-100",
      icon: (
        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    confirm: {
      wrapper:
        "bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 border-green-300 dark:border-green-700",
      title: "text-green-700 dark:text-green-300",
      body: "text-green-900 dark:text-green-100",
      icon: (
        <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      ),
    },
    default: {
      wrapper:
        "bg-gradient-to-br from-orange-50 to-orange-100 dark:from-[#F56948]/20 dark:to-[#FF8A6E]/10 border-orange-300 dark:border-[#F56948]/50",
      title: "text-[#F56948] dark:text-[#FF8A6E]",
      body: "text-orange-900 dark:text-orange-100",
      icon: (
        <svg className="w-4 h-4 text-[#F56948]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
    },
  };

  const s = schemeStyles[colorScheme];

  return (
    <div className="flex justify-center my-2">
      <div className={`max-w-sm w-full px-4 py-3 rounded-xl border-2 shadow-sm ${s.wrapper}`}>
        <div className="flex items-center justify-center gap-2 mb-1">
          {s.icon}
          <span className={`text-xs font-bold uppercase tracking-wide ${s.title}`}>
            {title}
          </span>
        </div>
        {text && (
          <p className={`text-xs text-center leading-relaxed ${s.body}`}>{text}</p>
        )}
        <p className="text-xs text-center mt-1 opacity-60 text-gray-500 dark:text-gray-400">
          {formatMessageTime(msg.created_at)}
        </p>
      </div>
    </div>
  );
};

const MessageList: React.FC<MessageListProps> = ({
  modifiedBookingMap,
  loadMoreMessages,
  showMore,
}) => {
  const bookingData = Array.isArray(modifiedBookingMap)
    ? modifiedBookingMap
    : [modifiedBookingMap];
  const { currentUser } = useSelector((state: RootState) => state.user);

  const myId = currentUser?.id;
  const isHost = currentUser?.is_host;
  const isStudent = currentUser?.is_student;

  const shouldShowMessage = (item: Message) => {
    const isCurrentUser = myId === item.sender_id;
    const isSystemMessage =
      (isHost && item.event_for === "host") ||
      (isStudent && item.event_for === "student");

    return {
      show: item.event_for === "both" || isCurrentUser || isSystemMessage,
      isSystemMessage,
    };
  };

  const [loading, setLoading] = useState(false);
  const nextPage = Array.isArray(modifiedBookingMap)
    ? modifiedBookingMap[0]?.meta?.next_page
    : modifiedBookingMap?.meta?.next_page;

  const totalMessages = bookingData.reduce((acc, response) => {
    return (
      acc +
      response.message.reduce((a, group) => a + group.messages.length, 0)
    );
  }, 0);

  const handleLoadMore = async () => {
    if (!nextPage) return;
    setLoading(true);
    try {
      await loadMoreMessages(nextPage);
    } finally {
      setLoading(false);
    }
  };

  // Skeleton loader
  if (showMore) {
    return (
      <div className="space-y-3 px-1">
        <div className="flex items-center justify-center">
          <Skeleton width={80} height={16} borderRadius={99} />
        </div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className={`flex items-end gap-2 ${index % 2 === 0 ? "" : "flex-row-reverse"}`}>
            <Skeleton circle width={32} height={32} />
            <Skeleton width={index % 2 === 0 ? 220 : 180} height={60} borderRadius={16} />
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (!bookingData || bookingData.length === 0 || totalMessages === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <div className="w-14 h-14 rounded-full bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center">
          <FiMessageSquare className="w-7 h-7 text-[#F56948]" />
        </div>
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
          No messages yet
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Start the conversation by sending a reply above.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {/* Section header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <FiMessageSquare className="w-4 h-4 text-[#F56948]" />
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Conversation
          </span>
        </div>
        <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
          {totalMessages} {totalMessages === 1 ? "message" : "messages"}
        </span>
      </div>

      {/* Load earlier button — top */}
      {nextPage && (
        <div className="flex justify-center mb-3">
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className="flex items-center gap-1.5 text-xs font-medium text-[#F56948] hover:text-[#E5593D] bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 px-4 py-2 rounded-full transition-all disabled:opacity-50"
          >
            {loading ? (
              <div className="w-3 h-3 border-2 border-[#F56948] border-t-transparent rounded-full animate-spin" />
            ) : (
              <FiChevronUp className="w-3.5 h-3.5" />
            )}
            {loading ? "Loading..." : "Load earlier messages"}
          </button>
        </div>
      )}

      {/* Message thread */}
      <div className="flex flex-col gap-1 max-h-[600px] overflow-y-auto custom-design px-1 pb-2">
        {bookingData.map((response, index) => (
          <div key={`response-${index}`} className="flex flex-col gap-1">
            {response.message.map((group, groupIndex) => (
              <div key={`group-${groupIndex}`} className="flex flex-col gap-3">

                {/* Date divider */}
                <div className="flex items-center justify-center my-2">
                  <div className="bg-gray-100 dark:bg-gray-800 px-4 py-1 rounded-full">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      {group.date}
                    </span>
                  </div>
                </div>

                {/* Messages */}
                {group.messages.map((msg, msgIndex) => {
                  const { show, isSystemMessage } = shouldShowMessage(msg);
                  if (!show) return null;

                  const isMe = myId === msg.sender_id;

                  // System / event message
                  if (isSystemMessage) {
                    return (
                      <SystemMessageBubble key={`msg-${msgIndex}`} msg={msg} />
                    );
                  }

                  // Regular chat bubble
                  return (
                    <div
                      key={`msg-${msgIndex}`}
                      className={`flex items-end gap-2 ${isMe ? "flex-row-reverse" : "flex-row"}`}
                    >
                      {/* Avatar */}
                      {!isMe && (
                        <div className="flex-shrink-0 mb-4">
                          {msg.sender.image ? (
                            <NcImage
                              src={msg.sender.image}
                              alt={msg.sender.name || "User"}
                              className="h-8 w-8 rounded-full object-cover ring-2 ring-white dark:ring-gray-800"
                            />
                          ) : (
                            <AvatarFallback name={msg.sender.name} />
                          )}
                        </div>
                      )}

                      {/* Bubble + meta */}
                      <div className={`flex flex-col max-w-[75%] gap-1 ${isMe ? "items-end" : "items-start"}`}>
                        {/* Sender name (received only) */}
                        {!isMe && (
                          <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 ml-1">
                            {msg.sender.name || "User"}
                          </span>
                        )}

                        {/* Bubble */}
                        <div
                          className={`rounded-2xl px-4 py-2.5 shadow-sm ${
                            isMe
                              ? "bg-[#F56948] text-white rounded-br-md"
                              : "bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 rounded-bl-md"
                          }`}
                        >
                          {/* File-only body (body.url set) */}
                          {msg?.body?.url ? (
                            <button
                              onClick={() => msg?.body?.url && window.open(msg.body.url, "_blank")}
                              className={`flex items-center gap-1.5 text-sm font-medium transition-opacity hover:opacity-80 ${
                                isMe ? "text-white" : "text-[#F56948]"
                              }`}
                            >
                              <RiAttachmentLine className="flex-shrink-0" />
                              <span className="truncate max-w-[200px]">{msg.body?.name}</span>
                            </button>
                          ) : (
                            <>
                              {/* Message text */}
                              {msg?.body?.name && (
                                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                                  {msg.body.name}
                                </p>
                              )}

                              {/* Attachments */}
                              {msg?.attachments && msg.attachments.length > 0 && (
                                <div className={`flex flex-wrap gap-1.5 ${msg?.body?.name ? "mt-2" : ""}`}>
                                  {msg.attachments.map((attachment: any, idx: number) => (
                                    <a
                                      key={idx}
                                      href={attachment.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-xl transition-all ${
                                        isMe
                                          ? "bg-white/20 hover:bg-white/30 text-white"
                                          : "bg-gray-100 dark:bg-gray-800 hover:bg-orange-50 dark:hover:bg-orange-900/20 text-gray-700 dark:text-gray-300 hover:text-[#F56948]"
                                      }`}
                                    >
                                      <RiAttachmentLine className="flex-shrink-0" />
                                      <span className="truncate max-w-[150px]">{attachment.name}</span>
                                    </a>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* Timestamp */}
                        <span className={`text-xs text-gray-400 dark:text-gray-500 px-1 ${isMe ? "text-right" : "text-left"}`}>
                          {formatMessageTime(msg.created_at)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MessageList;
